import React from "react";
import { useTranslation } from "react-i18next";
import { AlertsCta } from "./AlertsCta";
import { ArrivalResultCard } from "./ArrivalResultCard";
import { DepartureResultCard } from "./DepartureResultCard";
import { FeedUnavailableCard } from "./FeedUnavailableCard";
import { RecordPlate } from "./RecordPlate";
import { resolveBadgeState } from "./handler/badge.helper";
import { BotCheckState, FlightFixture } from "./types";

export interface ResultCardProps {
  fixtures: FlightFixture[];
  botCheckState: BotCheckState;
  onStartOptIn: (fixture: FlightFixture) => void;
  onRetryFeed: () => void;
  onLookupAnother: () => void;
}

export const ResultCard: React.FC<ResultCardProps> = ({
  fixtures,
  botCheckState,
  onStartOptIn,
  onRetryFeed,
  onLookupAnother,
}) => {
  const { t } = useTranslation("flightAlerts");
  const primary = fixtures[0];
  if (!primary) return null;
  const hasMultipleMatches = fixtures.length > 1;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onLookupAnother}
        className="self-start font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60"
      >
        {t("results.backLink")}
      </button>

      {hasMultipleMatches && (
        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
          {t("results.multipleMatchesNote", {
            count: fixtures.length,
            flightNumber: primary.flightNumber,
            date: primary.dateDisplay,
          })}
        </p>
      )}

      {fixtures.map((fixture) => (
        <React.Fragment key={fixture.id}>
          {fixture.feedUnavailable ? (
            <FeedUnavailableCard fixture={fixture} onRetry={onRetryFeed} />
          ) : (
            (() => {
              const badge = resolveBadgeState(fixture);
              return (
                <>
                  {fixture.direction === "arrival" ? (
                    <ArrivalResultCard fixture={fixture} badge={badge} />
                  ) : (
                    <DepartureResultCard fixture={fixture} badge={badge} />
                  )}

                  {!hasMultipleMatches && (
                    <RecordPlate
                      record={fixture.record}
                      direction={fixture.direction}
                    />
                  )}
                  <AlertsCta
                    badge={badge}
                    botCheckState={botCheckState}
                    direction={fixture.direction}
                    onStart={() => onStartOptIn(fixture)}
                  />
                </>
              );
            })()
          )}
        </React.Fragment>
      ))}

      {hasMultipleMatches && !primary.feedUnavailable && (
        <RecordPlate record={primary.record} direction={primary.direction} />
      )}
    </div>
  );
};
